import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers';

import { approveWorklogs } from '../api/time-tracking.api';
import { APPROVALS_QUERY_KEYS } from '../constants/approvals-query-keys';
import { getApprovalSelectionCountForMode } from '../helpers/approval-selection-counts.helper';
import { getSelectionCommittedEntryIds } from '../helpers/table-selection.helpers';
import { useApprovalsStore } from '../store/approvals.store';
import { GetApprovalFilterRequest } from '../types/dto/worklogs-approval.dto';
import {
  ApprovalConfirmMode,
  ApprovalSelection,
  ApprovalSelectionLevel,
  TimeEntryStatus,
} from '../types/time-tracking-approvals';
import { patchApprovalWorklogs } from '../utils/patch-approval-worklogs';
import { invalidateApprovalAggregates } from '../utils/time-tracking-query-invalidation';

interface ApproveWorklogsParams {
  selection: ApprovalSelection;
  filter: GetApprovalFilterRequest;
}

export const useApproveWorklogs = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.timeTracking');

  return useMutation({
    mutationFn: ({ selection, filter }: ApproveWorklogsParams) =>
      approveWorklogs({ selection, filter }),
    onSuccess: async (_, { selection }) => {
      if (selection.level === ApprovalSelectionLevel.Worklogs) {
        const { entries } = selection;
        // Patch only the changed worklog rows in place (status → Approved + both approved values),
        // instead of refetching every loaded worklog page (heavy + laggy when many are open).
        // The rounding must match `mapEntryToApproveWorklogRequest` so the patched rows show
        // exactly what the request wrote.
        const approvedMinutesById = new Map(
          entries.map(e => [
            e.id,
            {
              invoice: Math.round(e.approvedForInvoiceHours * 60),
              bill: Math.round(e.approvedForBillHours * 60),
            },
          ])
        );
        patchApprovalWorklogs({
          queryClient,
          entries,
          status: TimeEntryStatus.APPROVED,
          approvedMinutesById,
        });

        // Edits are now committed server-side — clear them so the page is no longer dirty.
        useApprovalsStore.getState().clearEditedEntries(entries.map(entry => entry.id));
      } else {
        // Project/member level approval changes worklogs that may not even be loaded —
        // targeted patching is impossible, so refetch the loaded worklog sections for
        // fresh statuses and hashes, and drop the aggregate-row selection.
        await queryClient.invalidateQueries({ queryKey: APPROVALS_QUERY_KEYS.worklogsAll });
        // The pending inline edits the selection covered rode along as worklog-level items,
        // so they are committed too — clear them (the refetched rows carry the saved values).
        useApprovalsStore.getState().clearEditedEntries(getSelectionCommittedEntryIds(selection));
        useApprovalsStore.getState().clearRowSelection();
      }

      // Refresh only the lightweight aggregates (summary tiles + project totals).
      await invalidateApprovalAggregates(queryClient);

      const count = getApprovalSelectionCountForMode(selection, ApprovalConfirmMode.Approve);
      const message =
        count === 1
          ? t('approvals.toasts.approvedSingle')
          : t('approvals.toasts.approvedBulk', { count });
      displayToastSuccess(message);
    },
  });
};
